import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { SVGFC } from '@repo/toolkit/types'
import { ClockIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { eachDayOfInterval, isBefore, isSameDay } from 'date-fns'
import { useMemo } from 'react'

type PlannerItemType<T extends string> = {
  id: number
  startingTime: Date
  endingTime: Date
  type: T
  disabled?: boolean

  color?: string
  backgroundColor?: string
  borderColor?: string
  showHours?: boolean
  showTime?: boolean // defaults to true; false hides the "HH:mm - HH:mm" line

  title: string
  content?: React.ReactNode
  icon?: SVGFC
  onClick?: () => void
}

type PlannerLabelType = {
  date: Date
  element: React.ReactNode
}

type Props<T extends string> = {
  startDate: Date
  endDate: Date
  disabled?: boolean
  items: PlannerItemType<T>[]
  labels?: PlannerLabelType[]
}

const getDayItems = <T extends string>(
  day: Date,
  items: PlannerItemType<T>[]
): PlannerItemType<T>[] => {
  return items
    .filter((item) => isSameDay(item.startingTime, day))
    .sort((a, b) => (isBefore(a.startingTime, b.startingTime) ? -1 : 1))
}

const getTimeWithBreakDeducted = (date: Date, breakInMinutes = 30) => {
  const adjustedDate = new Date(date)
  adjustedDate.setMinutes(adjustedDate.getMinutes() - breakInMinutes)

  return {
    hours: adjustedDate.getHours(),
    minutes: adjustedDate.getMinutes()
  }
}

const PlannerCard = <T extends string>({ item }: { item: PlannerItemType<T> }) => {
  const endingTimeWithBreakDeducted = getTimeWithBreakDeducted(item.endingTime)

  return (
    <div
      onClick={() => {
        if (item.onClick && !item.disabled) item.onClick()
      }}
      className={cn(
        'cursor-pointer border-2 border-transparent border-l-2 px-2 py-1 text-sm rounded-sm mx-1.5 first:mt-1.5 last:mb-1.5',
        { 'cursor-auto': item.disabled }
      )}
      style={{
        color: item.color,
        borderLeftColor: item.borderColor,
        backgroundColor: item.backgroundColor
      }}
    >
      <div className="flex justify-between items-center">
        <div>
          <Typography variant="label-medium" color={item.color} className="text-wrap w-40">
            {item.title}
            {item.showHours && (
              <>
                {' - '}
                {DateFormatter.getTimeDuration(
                  {
                    hours: new Date(item.startingTime).getHours(),
                    minutes: new Date(item.startingTime).getMinutes()
                  },
                  endingTimeWithBreakDeducted
                )}
              </>
            )}
          </Typography>

          {item.showTime !== false && (
            <>
              <div className="h-1.5" />
              <div className="flex items-center gap-0.5">
                <ClockIcon color={item.color} />
                <Typography variant="small-medium" color={item.color}>
                  {DateFormatter.getFormattedTime({
                    hours: new Date(item.startingTime).getHours(),
                    minutes: new Date(item.startingTime).getMinutes()
                  })}{' '}
                  -{' '}
                  {DateFormatter.getFormattedTime({
                    hours: new Date(item.endingTime).getHours(),
                    minutes: new Date(item.endingTime).getMinutes()
                  })}
                </Typography>
              </div>
            </>
          )}
        </div>
        {item.icon && <item.icon color={item.color} />}
      </div>
      {item.content ? (
        <>
          <div className="h-7" />
          {item.content}
        </>
      ) : (
        <div className="h-2" />
      )}
    </div>
  )
}

const WeeklyPlanner = <T extends string>({
  startDate,
  endDate,
  items,
  labels,
  disabled
}: Props<T>) => {
  const days = useMemo(
    () => eachDayOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate]
  )

  const dates = useMemo(() => {
    return days.map((day) => ({
      date: day,
      items: getDayItems(day, items)
    }))
  }, [days, items])

  return (
    <InnerBox className={' p-0 bg-white'}>
      <ScrollableDiv className="overflow-x-auto">
        <div
          className={cn('flex min-w-max pb-4', {
            'pointer-events-none ': disabled
          })}
        >
          {days.map((day) => {
            const date = dates.find((d) => isSameDay(d.date, day))?.items || []

            return (
              <div
                key={day.toISOString()}
                className="min-w-[160px] flex-shrink-0 border-r border-grey-light bg-white min-h-[600px]"
              >
                <div className="text-center font-semibold text-sm bg-white-active px-6 py-5">
                  {DateFormatter.DDDMYYYY(day)}
                </div>

                {labels
                  ?.filter((label) => isSameDay(label.date, day))
                  .map((label, index) => (
                    <div key={index} className="flex items-center justify-center py-2">
                      {label.element}
                    </div>
                  ))}
                <div className="flex flex-col gap-1.5">
                  {date.map((item) => (
                    <PlannerCard key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </ScrollableDiv>
    </InnerBox>
  )
}

export default WeeklyPlanner
